import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { API_URL } from "..";

const PrintBillPage = () => {
  const { id } = useParams();
  const [bill, setBill] = useState(null);

  useEffect(() => {
  fetch(`${API_URL}/api/purchase-bills/single/${id}`)
    .then(res => res.json())
    .then(data => {
      setBill(data.data);

      // Auto-print
      setTimeout(() => window.print(), 1000);

      // Auto-redirect after print (Chrome, Firefox, Edge support)
      window.onafterprint = () => {
        window.location.href = "/list-bills";
      };
    })
    .catch(err => console.error("Error loading bill:", err));

  // Cleanup to avoid memory leak warnings
  return () => {
    window.onafterprint = null;
  };
}, [id]);

  if (!bill)
    return <p style={{ textAlign: "center", marginTop: "50px" }}>Loading bill...</p>;

  const items = bill?.PurchaseBillItems || [];
  const expenses = bill?.PurchaseBillExpenses || [];

  // ✅ Calculate totals
  const totalItems = items.reduce((sum, i) => sum + Number(i.amount || 0), 0);
  const totalExpenses = expenses.reduce((sum, e) => sum + Number(e.expense_amount || 0), 0);
  const grandTotal = totalItems + totalExpenses;

  const styles = {
    container: {
      maxWidth: "900px",
      margin: "0 auto",
      padding: "40px",
      fontFamily: "Arial, sans-serif",
      color: "#000",
    },
    header: {
      textAlign: "center",
      marginBottom: "20px",
      borderBottom: "2px solid #000",
      paddingBottom: "10px",
    },
    infoSection: {
      display: "flex",
      justifyContent: "space-between",
      marginBottom: "25px",
    },
    table: {
      width: "100%",
      borderCollapse: "collapse",
      marginBottom: "20px",
      fontSize: "14px",
    },
    th: {
      border: "1px solid #000",
      backgroundColor: "#f4f4f4",
      padding: "8px",
      textAlign: "left",
    },
    td: {
      border: "1px solid #000",
      padding: "8px",
      textAlign: "left",
    },
    totalRow: {
      textAlign: "right",
      fontWeight: "bold",
      fontSize: "16px",
      marginTop: "10px",
    },
    footer: {
      textAlign: "center",
      marginTop: "40px",
      borderTop: "1px dashed #000",
      paddingTop: "10px",
      fontSize: "12px",
      color: "#555",
    },
    sectionTitle: {
      fontWeight: "bold",
      margin: "10px 0 5px",
      fontSize: "16px",
      textDecoration: "underline",
    },
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h2>Purchase Bill</h2>
        <p>Thank you for your business!</p>
      </div>

      {/* Bill Info */}
      <div style={styles.infoSection}>
        <div>
          <p><strong>Bill No:</strong> {bill.bill_number}</p>
          <p><strong>Date:</strong> {bill.date}</p>
          <p><strong>Status:</strong> {bill.status}</p>
        </div>
        <div style={{ textAlign: "right" }}>
          <p><strong>Supplier:</strong> {bill.Supplier?.name || "N/A"}</p>
          <p><strong>Email:</strong> {bill.Supplier?.email || "N/A"}</p>
          <p><strong>Phone:</strong> {bill.Supplier?.phone || "N/A"}</p>
        </div>
      </div>

      {/* Items Table */}
      <h4 style={styles.sectionTitle}>Bill Items</h4>
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>#</th>
            <th style={styles.th}>Item Name</th>
            <th style={styles.th}>Qty</th>
            <th style={styles.th}>Rate</th>
            <th style={styles.th}>Amount</th>
          </tr>
        </thead>
        <tbody>
          {items.length > 0 ? (
            items.map((item, i) => (
              <tr key={i}>
                <td style={styles.td}>{i + 1}</td>
                <td style={styles.td}>{item.item_name}</td>
                <td style={styles.td}>{item.quantity}</td>
                <td style={styles.td}>Rs. {Number(item.rate).toLocaleString()}</td>
                <td style={styles.td}>Rs. {Number(item.amount).toLocaleString()}</td>
              </tr>
            ))
          ) : (
            <tr>
              <td style={styles.td} colSpan="5" align="center">
                No items found
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* Expenses Table */}
      <h4 style={styles.sectionTitle}>Additional Expenses</h4>
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>#</th>
            <th style={styles.th}>Expense Name</th>
            <th style={styles.th}>Amount</th>
          </tr>
        </thead>
        <tbody>
          {expenses.length > 0 ? (
            expenses.map((exp, i) => (
              <tr key={i}>
                <td style={styles.td}>{i + 1}</td>
                <td style={styles.td}>{exp.expense_name}</td>
                <td style={styles.td}>Rs. {Number(exp.expense_amount).toLocaleString()}</td>
              </tr>
            ))
          ) : (
            <tr>
              <td style={styles.td} colSpan="3" align="center">
                No additional expenses
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* Totals */}
      <div style={styles.totalRow}>
        <p>Items Total: Rs. {totalItems.toLocaleString()}</p>
        <p>Expenses Total: Rs. {totalExpenses.toLocaleString()}</p>
        <p><strong>Grand Total: Rs. {grandTotal.toLocaleString()}</strong></p>
        <p>Paid: Rs. {Number(bill.paid_amount || 0).toLocaleString()}</p>
        <p style={{ color: "red" }}>Due: Rs. {Number(bill.due_amount || 0).toLocaleString()}</p>
      </div>

      {/* Remarks */}
      {bill.remarks && (
        <div style={{ marginTop: "20px" }}>
          <h4 style={styles.sectionTitle}>Remarks</h4>
          <p>{bill.remarks}</p>
        </div>
      )}

      {/* Footer */}
      <div style={styles.footer}>
        <p>Generated on {new Date().toLocaleString()}</p>
        <p>Powered by <strong>UniSoftwares Accounting Software</strong></p>
      </div>
    </div>
  );
};

export default PrintBillPage;
